import {ApolloCache} from '@apollo/client';
import {useFormik} from 'formik';
import React, {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router';
import {useQueryParam, StringParam} from 'use-query-params';
import * as Yup from 'yup';

import {useSigninMutation, useVerifyOtpMutation} from '@/api';
import CURRENT_USER from '@/api/queries/currentUser';
import {OtpInput} from '@/components/shared/OtpInput';

import {
  PositiveButton,
  BasicButton,
} from '../../../components/mypage/MypageLayout';

export const validateSchema = Yup.object().shape({
  email: Yup.string().trim().required('必須項目です'),
  password: Yup.string().trim().required('必須項目です'),
});

const Login = () => {
  const navigate = useNavigate();
  const [returnUrl] = useQueryParam('return_url', StringParam);
  // 会員登録の確認画面（signup/Confirmation.tsx）から、遷移時の state で渡されるお知らせ
  const notice = (useLocation().state as {notice?: string} | null)?.notice;

  // パスワードが通ると認証コードの入力へ進む（#159）。otpKey を変えると入力欄を空にして作り直す
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [otpKey, setOtpKey] = useState(0);
  const [otpMessage, setOtpMessage] = useState<{
    error?: string;
    notice?: string;
  }>({});

  const completeLogin = (cache: ApolloCache<unknown>, user: object) => {
    cache.writeQuery({query: CURRENT_USER, data: {currentUser: user}});
    window.location.href = returnUrl || '/';
  };

  const [signin, {data, loading}] = useSigninMutation({
    update: (cache, {data}) => {
      if (data.signin.error) {
        setOtpMessage({error: data.signin.error});
      } else if (data.signin.otpRequired) {
        setOtpMessage(
          step === 'otp' ? {notice: '認証コードを再送しました。'} : {},
        );
        setOtpKey((key) => key + 1);
        setStep('otp');
      } else {
        // 開発環境で OTP を省いたとき（SKIP_OTP）はここでログインが済んでいる
        completeLogin(cache, data.signin.user);
      }
    },
  });

  const [verifyOtp, {loading: verifying}] = useVerifyOtpMutation({
    update: (cache, {data}) => {
      if (data.verifyOtp.error) {
        const remaining = data.verifyOtp.remainingAttempts;
        setOtpMessage({
          error:
            remaining == null
              ? data.verifyOtp.error
              : `${data.verifyOtp.error}（残り${remaining}回）`,
        });
        setOtpKey((key) => key + 1);
      } else {
        completeLogin(cache, data.verifyOtp.user);
      }
    },
  });

  const formik = useFormik({
    validationSchema: validateSchema,
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: (values) => {
      void signin({variables: values});
    },
  });

  if (step === 'otp') {
    return (
      <div className="mx-auto mb-[90px] w-[400px] pt-[90px] text-center">
        <h1 className="mb-10 text-center text-2xl font-medium text-neutral-700">
          認証コードの入力
        </h1>

        <p className="mb-8 text-sm leading-relaxed text-neutral-700">
          ご登録のメールアドレスに6桁の認証コードを送りました。
          <br />
          メールに記載のコードを入力してください（有効期限10分）。
        </p>

        {otpMessage.notice && (
          <div className="mb-4 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-700">
            <p>{otpMessage.notice}</p>
          </div>
        )}

        {otpMessage.error && (
          <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            <p>{otpMessage.error}</p>
          </div>
        )}

        <OtpInput
          key={otpKey}
          disabled={verifying || loading}
          onComplete={(code) => {
            setOtpMessage({});
            void verifyOtp({variables: {code}});
          }}
        />

        {verifying && <p className="mt-4 text-sm text-neutral-500">認証中…</p>}

        <div className="mt-12 flex flex-col items-center gap-4 text-xs">
          <button
            type="button"
            className="text-primary"
            disabled={loading}
            onClick={() => void signin({variables: formik.values})}>
            認証コードを再送する
          </button>
          <button
            type="button"
            className="text-primary"
            onClick={() => {
              setOtpMessage({});
              setStep('password');
            }}>
            ログイン画面に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-[90px] w-[400px] pt-[90px] text-center">
      <h1 className="mb-[70px] text-center text-[26px] font-medium text-neutral-700">
        ログイン
      </h1>

      {notice && !data?.signin?.error && (
        <div className="mb-4 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-700">
          <p>{notice}</p>
        </div>
      )}

      {data?.signin?.error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          <p>{data.signin.error}</p>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            メールアドレス<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.email && formik.errors.email && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.email}
            </p>
          )}
        </div>
        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            パスワード<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.password && formik.errors.password && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.password}
            </p>
          )}
        </div>
        <div className="my-[15px] text-left text-xs leading-[1.17]">
          <Link to="/reset_password" className="text-primary">
            パスワードをお忘れの方はこちら
          </Link>
        </div>

        <div className="my-[60px] flex justify-center">
          <PositiveButton
            type="submit"
            size="large"
            loading={loading}
            disabled={loading || !formik.isValid}>
            メールアドレスでログイン
          </PositiveButton>
        </div>

        <div className="relative my-10 h-px border-t border-neutral-500 text-[13px]" />

        <p>アカウントをお持ちでない方はこちら</p>

        <div className="signup mt-4 flex justify-center">
          <BasicButton
            onClick={() => void navigate('/signup')}
            className="!h-14 !w-[153px] !rounded-[30px]">
            新規作成
          </BasicButton>
        </div>
      </form>
    </div>
  );
};

export {Login};
